import { HowTo, HowToStep, WithContext } from 'schema-dts';

const SCHEMA_IMAGE_BASE = 'https://picsart.com';

// schema.org HowToStep.image expects an absolute URL. Resolve relative paths
// against the canonical site origin; upgrade protocol-relative URLs to https
// (Google's structured-data tooling has been finicky with `//cdn.example`).
function resolveAbsoluteUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('//')) return `https:${url}`;
  return `${SCHEMA_IMAGE_BASE}${url.startsWith('/') ? url : `/${url}`}`;
}

export interface IHowToSchemaStep {
  title?: string;
  description?: unknown;
  image?: string;
}

export interface IBuildHowToJsonLdOptions {
  title?: string;
  label?: string;
  steps: IHowToSchemaStep[];
  rootImage?: string;
}

/**
 * Build a schema.org HowTo JSON-LD object from landing-page step data.
 * Returns `null` when there's not enough data to produce a valid schema
 * (missing name, no steps, or no step has usable text). Callers should
 * short-circuit rendering `<JsonLd />` on `null`.
 *
 * Fallback order for `name`: `title` (if string) → `label` (if string) → skip.
 * We deliberately do NOT fall back to `steps[0].title`, since that describes
 * the first step rather than the whole procedure.
 */
export function buildHowToJsonLd({
  title,
  label,
  steps,
  rootImage,
}: IBuildHowToJsonLdOptions): WithContext<HowTo> | null {
  const stringTitle = title && title.trim() ? title : '';
  const stringLabel = label && label.trim() ? label : '';
  const name = stringTitle || stringLabel;

  if (!name || steps.length === 0) {
    if (!name) {
      // Surfaced in prod too: HowTo SEO coverage is silently lost otherwise
      // and we got bitten by 29 broken pages already.
      console.warn(
        '[buildHowToJsonLd] Skipping HowTo schema: missing both `title` and `label`. HowTo SEO coverage will be lost for this page.',
      );
    }
    return null;
  }

  const jsonLdSteps: HowToStep[] = steps
    .map((step, index) => {
      const stepTitle = typeof step.title === 'string' ? step.title : undefined;
      // Intentional fallback: when description isn't a string we use the
      // step title as schema text. Drops empty steps but never emits an
      // invalid HowToStep with no `text`.
      const stepText = typeof step.description === 'string' ? step.description : stepTitle;
      if (!stepText) return null;
      const stepImage = resolveAbsoluteUrl(step.image);
      return {
        '@type': 'HowToStep',
        position: index + 1,
        ...(stepTitle ? { name: stepTitle } : {}),
        text: stepText,
        ...(stepImage ? { image: stepImage } : {}),
      } as HowToStep;
    })
    .filter((step): step is HowToStep => step !== null);

  if (jsonLdSteps.length === 0) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        '[buildHowToJsonLd] Skipping HowTo schema: all steps were filtered out (no usable `description` or `title`). Check the step data shape.',
      );
    }
    return null;
  }

  const absoluteRootImage = resolveAbsoluteUrl(rootImage);
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name,
    ...(absoluteRootImage ? { image: absoluteRootImage } : {}),
    step: jsonLdSteps,
  };
}
